// Bootstrap a guest JWT on first visit so the rest of the booking
// flow can call `/booking/*` (which all sit behind `agent.jwt:none`).
//
// Flow:
//   1. If a usable token already exists in sessionStorage, do nothing.
//   2. Otherwise POST /auth/guest-session with a stable per-browser
//      device_id (kept in localStorage so it survives tab reloads).
//   3. Store the returned `access_token` in sessionStorage under
//      `tickets_v2_token` — picked up by the axios interceptor.
//
// Errors here are non-fatal: the site still renders, and individual
// booking actions surface their own 401s with a "session lost" UX.

import { authGuestSession, uuidV4 } from "./ticketsBooking";
import { TICKETS_TOKEN_STORAGE_KEY } from "./apiV2";

const DEVICE_ID_STORAGE_KEY = "abs_device_id";
const TOKEN_EXPIRY_KEY = "tickets_v2_token_expires_at";

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!id) {
    id = uuidV4();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  }
  return id;
}

function tokenStillFresh() {
  const tok = sessionStorage.getItem(TICKETS_TOKEN_STORAGE_KEY);
  if (!tok) return false;
  const expIso = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expIso) return true; // unknown expiry — give the call a try
  return new Date(expIso).getTime() > Date.now() + 30_000;
}

export async function ensureGuestSession() {
  if (tokenStillFresh()) return;
  try {
    const res = await authGuestSession({
      deviceId: getOrCreateDeviceId(),
      deviceType: "web",
    });
    const data = res?.data?.data || {};
    if (data.access_token) {
      sessionStorage.setItem(TICKETS_TOKEN_STORAGE_KEY, data.access_token);
      if (Number.isFinite(data.expires_in)) {
        const expAt = new Date(Date.now() + data.expires_in * 1000).toISOString();
        sessionStorage.setItem(TOKEN_EXPIRY_KEY, expAt);
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[guestSession] bootstrap failed:", err?.response?.status ?? err?.message);
  }
}

export function clearGuestSession() {
  sessionStorage.removeItem(TICKETS_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
}
